import { AxiosError } from 'axios'
import dayjs from 'dayjs'
import { FC, useCallback, useEffect, useMemo } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import { ServerValidationError } from '@/api/shared/types'
import { useServerTimeStore } from '@/components/entities/common/model/server-time'
import {
  DEFAULT_DATE_ONLY_FORMAT,
  DEFAULT_TIMEZONE
} from '@/components/entities/requests/model/constants'
import { CalendarType } from '@/components/shared/datepicker/ui/Calendar.types'
import { CalendarDropdown } from '@/components/shared/datepicker/ui/CalendarDropdown'

type Props = {
  passError?: AxiosError<ServerValidationError> | null
}

const RequestDatePickerFormBlock: FC<Props> = ({ passError }) => {
  const {
    control,
    setValue,
    setError,
    clearErrors,
    formState: { errors }
  } = useFormContext<{ date: Date }>()
  const date = useWatch({ control, name: 'date' })

  const serverOffsetMs = useServerTimeStore(state => state.serverOffsetMs)

  const mskTodayKey = dayjs(Date.now() + serverOffsetMs)
    .tz(DEFAULT_TIMEZONE)
    .format(DEFAULT_DATE_ONLY_FORMAT)
  const mskToday = useMemo(() => dayjs(mskTodayKey).toDate(), [mskTodayKey])

  const applyDesiredDateServerError = useCallback(() => {
    const serverErrors = passError?.response?.data?.errors
    if (!serverErrors?.length) {
      return
    }

    const desiredDateError = serverErrors.find(
      error => error.field === 'desiredDate'
    )

    if (desiredDateError?.message) {
      setError('date', {
        type: 'server',
        message: desiredDateError.message
      })
    }
  }, [passError, setError])

  useEffect(() => {
    applyDesiredDateServerError()
  }, [applyDesiredDateServerError])

  useEffect(() => {
    if (!date) {
      setValue('date', mskToday)
    }
  }, [date, setValue, mskToday])

  const handleChangeDate = (nextDate?: Date) => {
    clearErrors('date')

    if (!nextDate) {
      return
    }

    const nextKey = dayjs(nextDate).format(DEFAULT_DATE_ONLY_FORMAT)

    if (nextKey < mskTodayKey) {
      return
    }

    setValue('date', nextDate, { shouldDirty: nextKey !== mskTodayKey })
  }

  return (
    <Controller
      name="date"
      control={control}
      render={({ field }) => (
        <CalendarDropdown
          type={CalendarType.Single}
          dataTestId="pass-date-input-id"
          date={field.value}
          onChange={handleChangeDate}
          fromMonth={dayjs(mskTodayKey).date(1).toDate()}
          today={mskToday}
          disabled={{ before: mskToday }}
          placeholder="Дата посещения"
          className="!h-56"
          short
          invalid={Boolean(errors.date)}
          invalidMessage={errors.date?.message}
        />
      )}
    />
  )
}

export { RequestDatePickerFormBlock }
